import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date");

export const fetchChapters = createServerFn({ method: "POST" }).handler(async () => {
  const { requireProfile } = await import("./session.server");
  const { listChapters } = await import("./chapters.server");
  const profile = await requireProfile();
  return listChapters(profile.id);
});

export const addChapter = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        subject: z.string().trim().min(1).max(40),
        chapter: z.string().trim().min(1).max(120),
        totalLectures: z.number().int().min(1).max(20),
        completedOn: dateSchema.nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { assertWithinQuota } = await import("./quota.server");
    const { createChapter } = await import("./chapters.server");
    const profile = await requireProfile();
    await assertWithinQuota(profile.id, "chapters");
    return createChapter(profile.id, data);
  });

export const updateChapterLectures = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({ chapterId: z.string().uuid(), totalLectures: z.number().int().min(1).max(20) })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { updateChapter } = await import("./chapters.server");
    const profile = await requireProfile();
    await updateChapter(profile.id, data.chapterId, data.totalLectures);
    return { ok: true };
  });

export const removeChapter = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => z.object({ chapterId: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { deleteChapter } = await import("./chapters.server");
    const profile = await requireProfile();
    await deleteChapter(profile.id, data.chapterId);
    return { ok: true };
  });

export const markLecture = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({ lectureId: z.string().uuid(), completedOn: dateSchema.nullable() })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { setLectureCompletion } = await import("./chapters.server");
    const profile = await requireProfile();
    await setLectureCompletion(profile.id, data.lectureId, data.completedOn);
    return { ok: true };
  });

export const completeRevisions = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({ revisionIds: z.array(z.string().uuid()).min(1).max(50), today: dateSchema })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { completeRevisionGroup } = await import("./chapters.server");
    const profile = await requireProfile();
    await completeRevisionGroup(profile.id, data.revisionIds, data.today);
    return { ok: true };
  });